import { useClerk } from "@clerk/clerk-react";
import { Logo } from "@the-drop/ui";
import { userHasPlatformAdminRole } from "@the-drop/types";
import { Link, useRouterState } from "@tanstack/react-router";
import { useWorkspaceMe } from "../workspace/workspace-me-context";

function HomeIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
      />
    </svg>
  );
}

function OrdersIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 10h16M4 14h10" />
    </svg>
  );
}

function InventoryIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
    </svg>
  );
}

function InvoicesIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
      />
    </svg>
  );
}

function UserIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
      />
    </svg>
  );
}

function SupportIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4h.01M11 8h2v4h-2V8z"
      />
    </svg>
  );
}

function LogoutIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
      />
    </svg>
  );
}

const navItems = [
  { icon: HomeIcon, label: "Dashboard", to: "/workspace/dashboard" as const },
  { icon: OrdersIcon, label: "Orders", to: "/workspace/orders" as const },
  { icon: InvoicesIcon, label: "Invoices", to: "/workspace/invoices" as const },
  { icon: InventoryIcon, label: "Inventory", to: "/workspace/inventory" as const },
  { icon: UserIcon, label: "Account", to: "/workspace/account" as const },
  { icon: SupportIcon, label: "Support", to: "/workspace/support" as const },
];

function AdminPlusIcon({ className }: { className?: string }) {
  return (
    <svg className={className} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
    </svg>
  );
}

export function DispensarySidebar() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { signOut } = useClerk();
  const me = useWorkspaceMe();
  const showAdminCreate = me != null && userHasPlatformAdminRole(me);

  return (
    <aside className="fixed left-0 top-0 z-30 flex h-screen w-[220px] flex-col border-r border-white/10 bg-[#2f5d50] px-3 py-6">
      <div className="mb-6 border-b border-white/10 px-2 pb-5">
        <Link to="/workspace/dashboard" className="inline-block">
          <Logo size="md" tone="light" />
        </Link>
      </div>
      <nav className="flex flex-1 flex-col gap-1 overflow-y-auto">
        {showAdminCreate ? (
          <Link
            to="/workspace/admin/create-dispensary"
            className={`mx-1 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
              pathname.startsWith("/workspace/admin/create-dispensary")
                ? "bg-white font-semibold text-[#111111]"
                : "text-white hover:bg-white/10"
            }`}
          >
            <AdminPlusIcon className="h-4 w-4 shrink-0" />
            <span className="truncate">New dispensary</span>
          </Link>
        ) : null}
        {navItems.map((item) => {
          const Icon = item.icon;
          const active = pathname === item.to || pathname.startsWith(`${item.to}/`);
          return (
            <Link
              key={item.to}
              to={item.to}
              className={`mx-1 flex min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium transition-colors duration-150 ${
                active
                  ? "bg-white font-semibold text-[#111111]"
                  : "text-white hover:bg-white/10"
              }`}
            >
              <Icon className="h-4 w-4 shrink-0" />
              <span className="truncate">{item.label}</span>
            </Link>
          );
        })}
      </nav>
      <div className="mt-auto border-t border-white/10 pt-4">
        <button
          type="button"
          onClick={() => void signOut({ redirectUrl: "/" })}
          className="mx-1 flex w-full min-h-[44px] cursor-pointer items-center gap-3 rounded-lg px-4 py-2.5 text-sm font-medium text-[#888888] transition-colors hover:bg-white/10 hover:text-white"
        >
          <LogoutIcon className="h-4 w-4 shrink-0" />
          Logout
        </button>
      </div>
    </aside>
  );
}
